<template>
  <div class="w-full flex flex-col">
    <div
      class="h-15 w-full flex justify-between items-center px-6 py-4 bg-neutral-background-default shrink-0 border-b border-neutral-border-default"
    >
      <div class="text-xl font-semibold">Projects</div>

      <UserSettingsFloatingMenu v-slot="{ show }">
        <button
          class="font-semibold text-sm uppercase items-center flex flex-nowrap text-nowrap gap-4"
          @click.stop="show"
        >
          <p
            class="font-medium text-base rounded-full size-10 flex justify-center items-center bg-primary-50 text-primary-500"
          >
            {{ user?.firstName?.charAt(0) }}{{ user?.lastName?.charAt(0) }}
          </p>
        </button>
      </UserSettingsFloatingMenu>
    </div>

    <div v-if="projects.length > 0" class="px-6 py-6">
      <div class="bg-white rounded-lg flex flex-col">
        <div class="px-6 py-4 border border-mid-neutral-50 rounded-t-lg">
          <div class="flex items-center justify-between">
            <div class="grid grid-cols-1">
              <input
                id="search-projects"
                v-model="searchQuery"
                type="text"
                name="search-projects"
                class="col-start-1 row-start-1 block w-80 rounded-md bg-white py-2 pr-10 pl-3 text-base text-dark-neutral-500 outline-1 -outline-offset-1 outline-mid-neutral-50 placeholder:text-mid-neutral-200 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-500 sm:pr-9 sm:text-sm/6"
                placeholder="Search for projects..."
              />
              <SearchIcon
                class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-gray-400 sm:size-4"
                aria-hidden="true"
              />
            </div>
            <CreateProject
              v-if="isAdmin"
              @project-created="
                (projectId) => {
                  expandedProjectId = projectId
                }
              "
            />
          </div>
        </div>

        <div v-if="filteredProjects.length > 0" class="flex flex-col">
          <div>
            <ProjectTable
              v-for="project in paginatedProjects"
              :key="project.id"
              :model-value="expandedProjectId === project.id"
              :project="project"
              :users="users.users.value"
              @update:model-value="
                (val: boolean) => {
                  expandedProjectId = val ? project.id : 0
                }
              "
            />
          </div>

          <div
            class="w-full flex items-center justify-between px-5 py-4 sticky bottom-0 bg-neutral-background-default border-r border-l border-b border-neutral-border-default rounded-b-lg z-50"
          >
            <p class="text-base font-medium text-neutral-text-title">
              Total Projects: {{ projects.length }}
            </p>
            <Pagination
              v-model:page="currentPage"
              v-model:items-per-page="itemsPerPage"
              :total-items="projects.length"
              items-label="Projects"
            />
          </div>
        </div>

        <div
          v-if="filteredProjects.length === 0"
          class="w-full flex items-center justify-center py-10"
        >
          <p class="text-dark-neutral-500">No projects matched your search.</p>
        </div>
      </div>
    </div>

    <div
      v-show="projects.length === 0 && !loading.isLoading.value"
      class="w-full flex items-center justify-center pt-28"
    >
      <div
        class="border-2 border-dashed bg-white border-slate-300 rounded-lg text-center px-80 py-32 space-y-2"
      >
        <CreateProjectIcon class="w-10 h-10 text-primary mx-auto" />
        <p class="pb-3">
          There are no projects yet, <br />start by creating one
        </p>
        <CreateProject
          v-if="isAdmin"
          @project-created="
            (projectId) => {
              expandedProjectId = projectId
            }
          "
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useProjects from '@app/views/Projects/use-projects'
import useLoading from '@app/composables/use-loading'
import ProjectTable from './Project/ProjectTable.vue'
import CreateProjectIcon from '@app/components/Icons/CreateProjectIcon.vue'
import useUsers from '@app/composables/use-users'
import CreateProject from './CreateProject.vue'
import { Role } from '@core/domain/types/role.type'
import { injectStrict } from '@app/utils/injectStrict'
import { AuthKey } from '@app/injectionKeys'
import SearchIcon from '@app/components/Icons/SearchIcon.vue'
import Pagination from '@app/components/Global/Pagination/Pagination.vue'
import UserSettingsFloatingMenu from '../Settings/UserSettings/UserSettingsFloatingMenu.vue'
import { useStorage } from '@vueuse/core'

const route = useRoute()
const router = useRouter()
const loading = useLoading()
const { projects, refresh } = useProjects()
const searchQuery = ref('')
const itemsPerPage = ref(5)
const currentPage = ref(1)

const expandedProjectId = useStorage<number>('expandedProjectId', 0)

const users = useUsers()
users.refresh()

const { user } = injectStrict(AuthKey)
const isAdmin = computed(() => user.value?.role === Role.ADMIN)

const filteredProjects = computed(() => {
  return projects.value
    .filter((project) =>
      project.name.toLowerCase().includes(searchQuery.value.toLowerCase()),
    )
    .sort((y, x) => Number(x.isFavorite) - Number(y.isFavorite))
})

const paginatedProjects = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredProjects.value.slice(start, end)
})

loading.start()

refresh().then(() => {
  loading.stop()
  if (route.query.projectId) {
    const projectId = Number(route.query.projectId)
    const project = projects.value.find((p) => p.id === projectId)
    if (project) {
      searchQuery.value = project.name
    }
    router.replace({ query: {} })
  }
})
</script>
